const MATCHING_NUMBER_LABEL = 'Matching Number';
const NON_MATCHING_SPECIAL_LABEL = 'Non-Matching Special Number';
const MATCHING_SPECIAL_LABEL = 'Matching Special Number';

const MATCH_NUMBER_CLASS = 'cmn-number--match';
const SPECIAL_NUMBER_CLASS = 'cmn-number--special';
const MATCH_SPECIAL_NUMBER_CLASS = 'cmn-number--match-special';

/**
 * Finds the sample number element used in the widget legend row.
 *
 * @param {Element} root Widget root element.
 * @param {string} labelText Legend row label text.
 * @return {HTMLElement|null}
 */
const getLegendSample = ( root, labelText ) => {

	const labelElement = Array.from( root.querySelectorAll( 'span, p, h1, h2, h3, h4, h5, h6' ) ).find(
		( element ) => element.textContent.trim() === labelText
	);

	if ( ! labelElement ) {

		return null;

	}

	const sampleElement = labelElement.previousElementSibling;

	if ( ! sampleElement || ! ( sampleElement instanceof HTMLElement ) ) {

		return null;

	}

	return sampleElement;

};

/**
 * Returns jss classes from an element.
 *
 * @param {HTMLElement|null} element Legend sample element.
 * @return {string[]}
 */
const getJssClasses = ( element ) => {

	if ( ! element ) {

		return [];

	}

	return Array.from( element.classList ).filter( ( className ) => className.startsWith( 'jss' ) );

};

/**
 * Returns the first item in `source` that does not exist in `excluded`.
 *
 * @param {string[]} source Source classes.
 * @param {string[]} excluded Excluded classes.
 * @return {string}
 */
const getUniqueClass = ( source, excluded ) => {

	const excludedSet = new Set( excluded );
	const foundClass = source.find( ( className ) => ! excludedSet.has( className ) );

	return foundClass || '';

};

/**
 * Adds stable classes to check-my-numbers spans.
 *
 * @param {Element} root Widget root element.
 * @return {void}
 */
const applyStableMatchClasses = ( root ) => {

	const matchingLegendSample = getLegendSample( root, MATCHING_NUMBER_LABEL );

	if ( ! matchingLegendSample ) {

		return;

	}

	const nonMatchingSpecialLegendSample = getLegendSample( root, NON_MATCHING_SPECIAL_LABEL );
	const matchingSpecialLegendSample = getLegendSample( root, MATCHING_SPECIAL_LABEL );
	const matchingClasses = getJssClasses( matchingLegendSample );
	const nonMatchingSpecialClasses = getJssClasses( nonMatchingSpecialLegendSample );
	const matchingSpecialClasses = getJssClasses( matchingSpecialLegendSample );

	const matchClass = getUniqueClass( matchingClasses, nonMatchingSpecialClasses );
	const specialClass = getUniqueClass( nonMatchingSpecialClasses, matchingClasses );
	const matchSpecialClass = getUniqueClass(
		matchingSpecialClasses,
		[ ...matchingClasses, ...nonMatchingSpecialClasses ]
	);

	if ( matchClass ) {

		root.querySelectorAll( `span.${ matchClass }` ).forEach( ( element ) => {

			element.classList.add( MATCH_NUMBER_CLASS );

		} );

	}

	if ( specialClass ) {

		root.querySelectorAll( `span.${ specialClass }` ).forEach( ( element ) => {

			element.classList.add( SPECIAL_NUMBER_CLASS );

		} );

	}

	if ( matchSpecialClass ) {

		root.querySelectorAll( `span.${ matchSpecialClass }` ).forEach( ( element ) => {

			element.classList.add( MATCH_SPECIAL_NUMBER_CLASS );

		} );

	}

};

/**
 * Scans page for widget results and applies stable classes.
 *
 * @return {void}
 */
const styleCheckMyNumbersWidget = () => {

	const resultHeadings = Array.from( document.querySelectorAll( 'h1, h2, h3, h4, h5, h6' ) ).filter(
		( heading ) => heading.textContent.trim() === 'Results'
	);

	resultHeadings.forEach( ( heading ) => {

		if ( heading.parentElement ) {

			applyStableMatchClasses( heading.parentElement );

		}

	} );

};

/**
 * Initializes runtime styling sync for dynamically rendered widget markup.
 *
 * @return {void}
 */
const initCheckMyNumbersStyles = () => {

	if ( ! document.body ) {

		return;

	}

	let frameId = 0;

	/**
	 * Schedules a single styling pass for this animation frame.
	 *
	 * @return {void}
	 */
	const run = () => {

		if ( frameId ) {

			return;

		}

		frameId = window.requestAnimationFrame( () => {

			frameId = 0;
			styleCheckMyNumbersWidget();

		} );

	};

	run();

	const observer = new MutationObserver( run );
	observer.observe( document.body, { childList: true, subtree: true } );

	window.addEventListener( 'load', run );

};

initCheckMyNumbersStyles();
